/*
 * Copyright 2026 Klyph Contributors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

package xyz.hyli.klyph

import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.produceState
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.TextUnit

/**
 * A Text composable within [SubsetFontProvider] scope that automatically loads font slices
 * and applies them character-by-character.
 *
 * This is the recommended way to use Klyph for font subsetting. It's fully compatible with
 * the standard Material3 Text API and automatically uses the provider from the
 * [SubsetFontProvider] scope.
 *
 * Internally builds an AnnotatedString where each character gets the appropriate font slice
 * based on unicode-range matching. Unlike combining all fonts into one FontFamily (which
 * doesn't work in Compose), this renders each character with its own FontFamily containing
 * only the necessary font slice.
 *
 * Example:
 * ```
 * SubsetFontProvider(provider = CssUrlFontDescriptorProvider("https://example.com/fonts.css")) {
 *     SubsetText(
 *         text = "Hello 世界!",
 *         fontSize = 20.sp,
 *         fontWeight = FontWeight.Bold
 *     )
 * }
 * ```
 *
 * @param text The text to display.
 * @param modifier Modifier to be applied to the text.
 * @param color The color of the text.
 * @param fontSize The font size of the text.
 * @param fontStyle The font style (normal, italic).
 * @param fontWeight The font weight (normal, bold, etc.).
 * @param fontFamily Optional FontFamily to use as a fallback for characters not covered by subset fonts.
 * @param letterSpacing The spacing between characters.
 * @param textDecoration The decoration to apply (underline, strikethrough).
 * @param textAlign The alignment of the text.
 * @param lineHeight The height of each line.
 * @param overflow How to handle text overflow.
 * @param softWrap Whether the text should break at soft line breaks.
 * @param maxLines The maximum number of lines.
 * @param minLines The minimum number of lines.
 * @param onTextLayout Callback for text layout result.
 * @param style The text style to apply.
 */
@Composable
fun SubsetFontScope.SubsetText(
    text: String,
    modifier: Modifier = Modifier,
    color: Color = Color.Unspecified,
    fontSize: TextUnit = TextUnit.Unspecified,
    fontStyle: FontStyle? = null,
    fontWeight: FontWeight? = null,
    fontFamily: FontFamily? = this.fontFamily,
    letterSpacing: TextUnit = TextUnit.Unspecified,
    textDecoration: TextDecoration? = null,
    textAlign: TextAlign? = null,
    lineHeight: TextUnit = TextUnit.Unspecified,
    overflow: TextOverflow = TextOverflow.Clip,
    softWrap: Boolean = true,
    maxLines: Int = Int.MAX_VALUE,
    minLines: Int = 1,
    onTextLayout: (TextLayoutResult) -> Unit = {},
    style: TextStyle = LocalTextStyle.current
) = SubsetText(
    text = text,
    modifier = modifier,
    color = color,
    fontSize = fontSize,
    fontStyle = fontStyle,
    fontWeight = fontWeight,
    fontFamily = fontFamily,
    letterSpacing = letterSpacing,
    textDecoration = textDecoration,
    textAlign = textAlign,
    lineHeight = lineHeight,
    overflow = overflow,
    softWrap = softWrap,
    maxLines = maxLines,
    minLines = minLines,
    onTextLayout = onTextLayout,
    style = style,
    provider = provider
)


/**
 * A Text composable that automatically loads font slices and applies them character-by-character.
 *
 * This is a standalone version that requires an explicit FontDescriptorProvider. For better ergonomics,
 * prefer using the scoped version within [SubsetFontProvider].
 *
 * This composable is fully compatible with the standard Material3 Text API. It internally
 * builds an AnnotatedString where each character gets the appropriate font slice based on
 * unicode-range matching.
 *
 * Unlike the standard approach of combining all fonts into one FontFamily (which doesn't work
 * in Compose), this renders each character with its own FontFamily containing only the
 * necessary font slice.
 *
 * Example:
 * ```
 * SubsetText(
 *     text = "Hello 世界!",
 *     provider = CssUrlFontDescriptorProvider("https://example.com/fonts.css"),
 *     fontSize = 20.sp
 * )
 * ```
 *
 * @param text The text to display.
 * @param modifier Modifier to be applied to the text.
 * @param color The color of the text.
 * @param fontSize The font size of the text.
 * @param fontStyle The font style (normal, italic).
 * @param fontWeight The font weight (normal, bold, etc.).
 * @param fontFamily Optional FontFamily to use as a fallback for characters not covered by subset fonts.
 * @param letterSpacing The spacing between characters.
 * @param textDecoration The decoration to apply (underline, strikethrough).
 * @param textAlign The alignment of the text.
 * @param lineHeight The height of each line.
 * @param overflow How to handle text overflow.
 * @param softWrap Whether the text should break at soft line breaks.
 * @param maxLines The maximum number of lines.
 * @param minLines The minimum number of lines.
 * @param onTextLayout Callback for text layout result.
 * @param style The text style to apply.
 * @param provider The FontDescriptorProvider that supplies parsed font descriptors.
 */
@Composable
fun SubsetText(
    text: String,
    modifier: Modifier = Modifier,
    color: Color = Color.Unspecified,
    fontSize: TextUnit = TextUnit.Unspecified,
    fontStyle: FontStyle? = null,
    fontWeight: FontWeight? = null,
    fontFamily: FontFamily? = null,
    letterSpacing: TextUnit = TextUnit.Unspecified,
    textDecoration: TextDecoration? = null,
    textAlign: TextAlign? = null,
    lineHeight: TextUnit = TextUnit.Unspecified,
    overflow: TextOverflow = TextOverflow.Clip,
    softWrap: Boolean = true,
    maxLines: Int = Int.MAX_VALUE,
    minLines: Int = 1,
    onTextLayout: (TextLayoutResult) -> Unit = {},
    style: TextStyle = LocalTextStyle.current,
    provider: FontDescriptorProvider
) {
    // Fetch and cache the font descriptors from provider
    val descriptors by produceState(emptyList(), provider) {
        try {
            value = provider.getDescriptors()
        } catch (e: Exception) {
            KlyphLogger.error("SubsetText", "Failed to load descriptors from provider", e)
            value = emptyList()
        }
    }

    // Build annotated string with per-character font slices
    val annotatedString = rememberSubsetAnnotatedString(
        descriptors = descriptors,
        text = text,
        requestedWeight = fontWeight,
        requestedStyle = fontStyle
    )

    Text(
        text = annotatedString,
        modifier = modifier,
        color = color,
        fontSize = fontSize,
        fontStyle = fontStyle,
        fontWeight = fontWeight,
        fontFamily = fontFamily,
        letterSpacing = letterSpacing,
        textDecoration = textDecoration,
        textAlign = textAlign,
        lineHeight = lineHeight,
        overflow = overflow,
        softWrap = softWrap,
        maxLines = maxLines,
        minLines = minLines,
        onTextLayout = onTextLayout,
        style = style
    )
}
